import React, {
  FC,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArchivalAidMetadata, ArchivalUnitCopy, EadNode } from 'types';
import { useEadTreeContext } from './EadTreeCtx';
import EadItem from './EadItem';
import { Theme, createStyles, makeStyles } from '@material-ui/core';
import { levelIcon } from '../grid-hooks/breadcrumbs-hook';
import { ListVariants, useVariantContext } from './VariantCtx';
import ArchivalUnitItem from './ArchivalUnitItem';
import { trimLabel } from '../upper-panel/grid-upper-panel';

import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { fetchAidChildren } from './fetch-with-cache';

type Props = {
  aid: ArchivalAidMetadata;
  level: number;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    active: {
      backgroundColor: theme.palette.primary.dark,
      color: 'white!important',
      padding: '0px 4px',
    },
    link: {
      color: 'white',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    icon: {
      marginRight: 4,
      fontSize: 11,
    },

    listItem: {
      fontSize: 13,
      lineHeight: '2em',
      fontWeight: 500,
      color: theme.palette.grey[300],
    },
    listItemActive: {
      fontSize: 13,
      lineHeight: '2em',
      fontWeight: 500,
      color: theme.palette.primary.light,
    },
    itemWrapper: {
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
  })
);
const variantToPath: Record<ListVariants, string> = {
  aids: 'pomucky',
  funds: 'fondy',
};
const AidItem: FC<Props> = ({ aid, level = 1 }) => {
  const { currentArchivalAid, childrenViewHeap, crumbs } = useEadTreeContext();
  const variant = useVariantContext();
  const { id, ead } = useParams<{
    id: string;
    ead: string;
  }>();

  const [children, setChildren] = useState<(EadNode | ArchivalUnitCopy)[]>([]);

  const isActive = useMemo(() => {
    return (
      currentArchivalAid?.id === aid?.id ||
      crumbs?.some((c) => c.id === aid?.id)
    );
  }, [aid?.id, currentArchivalAid?.id, crumbs]);

  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (isActive) {
      setTimeout(() => {
        const el = document.getElementById('G_LEFT_PANEL');
        const top = (ref.current?.offsetTop ?? 0) - 100;
        el?.scrollTo({
          top,
          behavior: 'smooth',
        });
      }, 10);
    }
  }, [ref, isActive]);

  useEffect(() => {
    (async () => {
      if (
        isActive &&
        !currentArchivalAid?.eadNode &&
        !ead &&
        (!aid.hasEadNode || aid.eadNode)
      ) {
        const r = await fetchAidChildren(aid);
        setChildren(r.items ?? []);
      } else if (isActive) {
        setChildren(
          ((currentArchivalAid?.eadNode?.children ??
            childrenViewHeap?.[aid?.id] ??
            []) as unknown) as EadNode[]
        );
      }
    })();
  }, [
    aid,
    childrenViewHeap,
    currentArchivalAid?.eadNode,
    currentArchivalAid?.eadNode?.children,
    isActive,
    ead,
  ]);

  const classes = useStyles();
  const Icon = levelIcon['aid'];
  const link = `/${variantToPath[variant!]}/${level}/${aid?.id}${
    ead === 'ead' && (aid?.hasEadNode || aid?.eadNode) ? '/ead' : ''
  }`;

  const listItemClass =
    isActive && aid?.id === id ? classes.listItemActive : classes.listItem;

  return (
    <div ref={ref} style={{ paddingLeft: 10 * level }}>
      <Link className={`${classes.link} ${listItemClass}`} to={link}>
        <div className={classes.itemWrapper}>
          <div>
            <span>
              <Icon className={classes.icon} />
            </span>
            {trimLabel(aid?.name ?? '')}
          </div>
          {aid?.hasChildren ? (
            isActive ? (
              <ExpandLessIcon classes={{ root: listItemClass }} />
            ) : (
              <ExpandMoreIcon classes={{ root: listItemClass }} />
            )
          ) : null}
        </div>
      </Link>
      {/* <br /> */}
      {isActive &&
        (children ?? []).map((ch) => {
          if (currentArchivalAid?.eadNode) {
            return <EadItem node={ch as EadNode} level={level + 1} />;
          } else {
            return (
              <ArchivalUnitItem
                archivalUnitCopy={ch as ArchivalUnitCopy}
                level={level + 1}
              />
            );
          }
        })}
    </div>
  );
};

export default AidItem;
